import React, { useState, useEffect, useRef } from 'react';
import { Send, MessageSquare, User, ArrowLeft, Plus, Search, X } from 'lucide-react';
import { toast } from 'sonner';
import { API, useAuth } from '../../App';

const MessagesPage = () => {
  const { user } = useAuth();
  const [conversations, setConversations] = useState([]);
  const [selectedClient, setSelectedClient] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [sending, setSending] = useState(false);
  const [showNewChat, setShowNewChat] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [contactSearch, setContactSearch] = useState('');
  const [loadingContacts, setLoadingContacts] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    fetchConversations();
    const interval = setInterval(fetchConversations, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (selectedClient) {
      fetchMessages(selectedClient.client_id);
    }
  }, [selectedClient]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchConversations = async () => {
    try {
      const response = await API.get('/admin/conversations');
      setConversations(response.data);
    } catch (error) {
      console.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchMessages = async (clientId) => {
    setLoadingMessages(true);
    try {
      const response = await API.get(`/admin/messages/${clientId}`);
      setMessages(response.data);
      setConversations(prev => 
        prev.map(c => c.client_id === clientId ? { ...c, unread_count: 0 } : c)
      );
    } catch (error) {
      toast.error('Failed to load messages');
    } finally {
      setLoadingMessages(false);
    }
  };

  const fetchContacts = async () => {
    setLoadingContacts(true);
    try {
      const response = await API.get('/admin/messaging-contacts');
      setContacts(response.data);
    } catch (error) {
      toast.error('Failed to load contacts');
    } finally {
      setLoadingContacts(false);
    }
  };

  const openNewChat = () => {
    setShowNewChat(true);
    setContactSearch('');
    fetchContacts();
  };

  const startConversation = (contact) => {
    setShowNewChat(false);
    const existing = conversations.find(c => c.client_id === contact.id);
    if (existing) {
      setSelectedClient(existing);
    } else {
      const newConv = {
        client_id: contact.id,
        client_name: contact.full_name,
        client_email: contact.email,
        client_role: contact.role,
        last_message: null,
        last_message_time: null,
        unread_count: 0,
      };
      setConversations(prev => [newConv, ...prev]);
      setSelectedClient(newConv);
    }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || !selectedClient) return;

    setSending(true);
    try {
      const response = await API.post('/messages', { 
        content: newMessage,
        recipient_id: selectedClient.client_id
      });
      setMessages(prev => [...prev, response.data]);
      setNewMessage('');
      setConversations(prev => {
        const updated = prev.map(c => 
          c.client_id === selectedClient.client_id 
            ? { ...c, last_message: newMessage, last_message_time: new Date().toISOString() }
            : c
        );
        return updated.sort((a, b) => 
          new Date(b.last_message_time || 0) - new Date(a.last_message_time || 0)
        );
      });
    } catch (error) {
      toast.error('Failed to send message');
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const now = new Date();
    const isToday = date.toDateString() === now.toDateString();
    if (isToday) {
      return date.toLocaleTimeString('en-GB', { hour: '2-digit', minute: '2-digit' });
    }
    return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  const isMyMessage = (message) => message.sender_id === user?.id;
  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  const filteredContacts = contacts.filter(c =>
    c.full_name?.toLowerCase().includes(contactSearch.toLowerCase()) ||
    c.email?.toLowerCase().includes(contactSearch.toLowerCase())
  );

  const roleBadge = (role) => {
    if (role === 'coach') return <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#233C5A]/10 text-[#233C5A]">Coach</span>;
    return <span className="text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded bg-[#2C5530]/10 text-[#2C5530]">Client</span>;
  };

  const avatarColor = (role) => role === 'coach' ? '#233C5A' : '#2C5530';

  return (
    <div className="h-[calc(100vh-120px)] flex" data-testid="admin-messages-page">
      {/* Conversations List */}
      <div className={`w-full md:w-80 border-r border-[#E7E5E4] bg-white flex flex-col ${
        selectedClient ? 'hidden md:flex' : 'flex'
      }`}>
        <div className="p-4 border-b border-[#E7E5E4] flex items-center justify-between">
          <div>
            <h1 className="font-['Playfair_Display'] text-xl text-[#1A1A1A]">Messages</h1>
            {totalUnread > 0 && (
              <p className="text-sm text-[#BC562B]">{totalUnread} unread</p>
            )}
          </div>
          <button
            onClick={openNewChat}
            data-testid="new-message-btn"
            className="p-2 bg-[#2C5530] text-white rounded-lg hover:bg-[#1E3A21] transition-colors"
            title="New message"
          >
            <Plus className="w-5 h-5" />
          </button>
        </div>

        {/* New Chat Contact Picker */}
        {showNewChat && (
          <div className="border-b border-[#E7E5E4] bg-[#F9F7F4]">
            <div className="p-3 flex items-center gap-2">
              <Search className="w-4 h-4 text-[#A8A29E]" />
              <input
                type="text"
                value={contactSearch}
                onChange={(e) => setContactSearch(e.target.value)}
                placeholder="Search clients & coaches..."
                className="flex-1 bg-transparent text-sm outline-none placeholder:text-[#A8A29E]"
                autoFocus
                data-testid="contact-search-input"
              />
              <button onClick={() => setShowNewChat(false)} className="p-1 text-[#A8A29E] hover:text-[#1A1A1A]">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="max-h-60 overflow-y-auto">
              {loadingContacts ? (
                <div className="p-4 text-center text-sm text-[#A8A29E]">Loading...</div>
              ) : filteredContacts.length === 0 ? (
                <div className="p-4 text-center text-sm text-[#A8A29E]">No contacts found</div>
              ) : (
                filteredContacts.map(contact => (
                  <button
                    key={contact.id}
                    onClick={() => startConversation(contact)}
                    className="w-full p-3 text-left hover:bg-[#F2F0E9] transition-colors flex items-center gap-3"
                    data-testid={`contact-${contact.id}`}
                  >
                    <div className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: avatarColor(contact.role) + '15' }}>
                      <span className="text-sm font-semibold" style={{ color: avatarColor(contact.role) }}>
                        {contact.full_name?.charAt(0) || '?'}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-semibold text-[#1A1A1A] truncate">{contact.full_name}</span>
                        {roleBadge(contact.role)}
                      </div>
                      <p className="text-xs text-[#A8A29E] truncate">{contact.email}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        )}

        <div className="flex-1 overflow-y-auto">
          {loading ? (
            <div className="flex items-center justify-center h-32">
              <div className="w-6 h-6 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin"></div>
            </div>
          ) : conversations.length === 0 ? (
            <div className="p-4 text-center text-[#737373]">
              <MessageSquare className="w-8 h-8 mx-auto mb-2 text-[#E7E5E4]" />
              <p className="text-sm">No conversations yet</p>
              <p className="text-xs text-[#A8A29E] mt-1">Click + to start a new message</p>
            </div>
          ) : (
            conversations.map((conv, index) => (
              <button
                key={conv.client_id}
                onClick={() => setSelectedClient(conv)}
                className={`w-full p-4 text-left border-b border-[#E7E5E4] hover:bg-[#F2F0E9] transition-colors ${
                  selectedClient?.client_id === conv.client_id ? 'bg-[#F2F0E9]' : ''
                }`}
                data-testid={`conversation-${index}`}
              >
                <div className="flex items-start gap-3">
                  <div className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0" style={{ backgroundColor: avatarColor(conv.client_role) + '15' }}>
                    <span className="font-semibold" style={{ color: avatarColor(conv.client_role) }}>
                      {conv.client_name?.charAt(0) || '?'}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start">
                      <div className="flex items-center gap-2 min-w-0">
                        <p className="font-semibold text-[#1A1A1A] truncate">{conv.client_name}</p>
                        {roleBadge(conv.client_role)}
                      </div>
                      {conv.unread_count > 0 && (
                        <span className="bg-[#BC562B] text-white text-xs px-2 py-0.5 rounded-full flex-shrink-0">
                          {conv.unread_count}
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-[#737373] truncate">{conv.last_message || 'No messages'}</p>
                    <p className="text-xs text-[#737373] mt-1">{formatTime(conv.last_message_time)}</p>
                  </div>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* Chat Area */}
      <div className={`flex-1 flex flex-col bg-[#FAFAF9] ${
        selectedClient ? 'flex' : 'hidden md:flex'
      }`}>
        {selectedClient ? (
          <>
            {/* Chat Header */}
            <div className="p-4 bg-white border-b border-[#E7E5E4] flex items-center gap-3">
              <button
                onClick={() => setSelectedClient(null)}
                className="md:hidden p-2 hover:bg-[#F2F0E9] transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <div className="w-10 h-10 rounded-full flex items-center justify-center" style={{ backgroundColor: avatarColor(selectedClient.client_role) + '15' }}>
                <User className="w-5 h-5" style={{ color: avatarColor(selectedClient.client_role) }} />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <p className="font-semibold text-[#1A1A1A]">{selectedClient.client_name}</p>
                  {roleBadge(selectedClient.client_role)}
                </div>
                <p className="text-sm text-[#737373]">{selectedClient.client_email}</p>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
              {loadingMessages ? (
                <div className="flex items-center justify-center h-full">
                  <div className="w-8 h-8 border-2 border-[#2C5530] border-t-transparent rounded-full animate-spin"></div>
                </div>
              ) : messages.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <MessageSquare className="w-12 h-12 text-[#E7E5E4] mb-4" />
                  <p className="text-[#737373]">No messages yet</p>
                  <p className="text-sm text-[#A8A29E]">Send the first message to {selectedClient.client_name}</p>
                </div>
              ) : (
                <>
                  {messages.map((message, index) => (
                    <div
                      key={message.id}
                      className={`flex ${isMyMessage(message) ? 'justify-end' : 'justify-start'}`}
                      data-testid={`message-${index}`}
                    >
                      <div
                        className={`max-w-[70%] ${
                          isMyMessage(message)
                            ? 'bg-[#2C5530] text-white'
                            : 'bg-white border border-[#E7E5E4] text-[#1A1A1A]'
                        } p-4`}
                      >
                        <p className="whitespace-pre-wrap">{message.content}</p>
                        <p className={`text-xs mt-2 ${isMyMessage(message) ? 'text-white/60' : 'text-[#737373]'}`}>
                          {formatTime(message.created_at)}
                        </p>
                      </div>
                    </div>
                  ))}
                  <div ref={messagesEndRef} />
                </>
              )}
            </div>

            {/* Input */}
            <form onSubmit={handleSend} className="p-4 bg-white border-t border-[#E7E5E4]">
              <div className="flex gap-3">
                <textarea
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Type your message..."
                  rows={2}
                  className="flex-1 resize-none"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' && !e.shiftKey) {
                      e.preventDefault();
                      handleSend(e);
                    }
                  }}
                  data-testid="message-input"
                />
                <button
                  type="submit"
                  disabled={sending || !newMessage.trim()}
                  className="self-end bg-[#2C5530] text-white px-6 py-3 font-semibold hover:bg-[#1E3A21] transition-colors disabled:opacity-50 flex items-center gap-2"
                  data-testid="send-message-btn"
                >
                  <Send className="w-4 h-4" />
                  Send
                </button>
              </div>
            </form>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-center p-8">
            <MessageSquare className="w-16 h-16 text-[#E7E5E4] mb-4" />
            <h2 className="font-['Playfair_Display'] text-xl text-[#1A1A1A] mb-2">Select a Conversation</h2>
            <p className="text-[#737373] mb-4">Choose a client or coach from the list to view messages</p>
            <button
              onClick={openNewChat}
              data-testid="new-message-cta"
              className="px-4 py-2 bg-[#2C5530] text-white rounded-lg font-semibold text-sm hover:bg-[#1E3A21] transition-colors flex items-center gap-2"
            >
              <Plus className="w-4 h-4" /> New Message
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagesPage;
